import { useCallback, useRef, useEffect } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Route prefetch registry - maps route patterns to their dynamic import functions.
 */
const PREFETCH_MAP: Record<string, () => Promise<unknown>> = {
  "/learn": () => import("@/pages/LearnIndex"),
  "/browse": () => import("@/pages/BrowseCourses"),
  "/what-would-you-do": () => import("@/pages/WhatWouldYouDoPage"),
  "/quiz-learn": () => import("@/pages/QuizLearn"),
  "course": () => import("@/pages/LearnCourse"),
  "chapter": () => import("@/pages/LearnRouter"),
};

// Track prefetched items to avoid duplicate requests
const prefetchedRoutes = new Set<string>();
const prefetchedData = new Set<string>();

function resolvePrefetchKey(path: string): string | null {
  if (PREFETCH_MAP[path]) return path;
  if (path.startsWith("/course/")) return "course";
  if (path.startsWith("/learn/") && path !== "/learn") return "chapter";
  return null;
}

function prefetchRoute(path: string): void {
  const key = resolvePrefetchKey(path);
  if (!key || prefetchedRoutes.has(key)) return;
  
  const importFn = PREFETCH_MAP[key];
  if (!importFn) return;
  
  prefetchedRoutes.add(key);
  importFn().catch(() => prefetchedRoutes.delete(key));
}

/**
 * OPTIMIZED: Prefetch chapter data (chapter + flashcards) in PARALLEL.
 * Call this on hover/focus of chapter links for instant navigation.
 */
function prefetchChapterData(queryClient: QueryClient, chapterId: string): void {
  const key = `chapter-data-${chapterId}`;
  if (prefetchedData.has(key)) return;
  prefetchedData.add(key);

  // Prefetch JS chunk for learner
  prefetchRoute(`/learn/${chapterId}`);

  // Prefetch chapter with flashcards - PARALLEL queries
  void queryClient.prefetchQuery({
    queryKey: ["chapter", chapterId],
    queryFn: async () => {
      const [chapterRes, flashcardsRes] = await Promise.all([
        supabase.from("chapters").select("*").eq("id", chapterId).maybeSingle(),
        supabase.from("flashcards").select("*").eq("chapter_id", chapterId).order("order_index", { ascending: true }),
      ]);

      if (chapterRes.error) throw chapterRes.error;
      if (!chapterRes.data) return null;
      if (flashcardsRes.error) throw flashcardsRes.error;

      return {
        ...chapterRes.data,
        flashcards: flashcardsRes.data || [],
      };
    },
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * OPTIMIZED: Prefetch ALL course data in PARALLEL for instant page load.
 * Fetches course, chapters, and flashcard titles simultaneously.
 */
function prefetchCourseData(queryClient: QueryClient, courseId: string): void {
  const key = `course-data-${courseId}`;
  if (prefetchedData.has(key)) return;
  prefetchedData.add(key);

  // Prefetch JS chunk
  prefetchRoute(`/course/${courseId}`);

  // SINGLE parallel prefetch for course page data
  void queryClient.prefetchQuery({
    queryKey: ["course-with-flashcards", courseId],
    queryFn: async () => {
      // Fetch course and chapters in parallel
      const [courseRes, chaptersRes] = await Promise.all([
        supabase.from("courses").select("id, title, description, cover_image_url, is_published, category, tags").eq("id", courseId).maybeSingle(),
        supabase.from("chapters").select("id, course_id, title, description, order_index").eq("course_id", courseId).order("order_index", { ascending: true }),
      ]);

      if (courseRes.error) throw courseRes.error;
      if (!courseRes.data) return null;

      const chapters = chaptersRes.data || [];
      const chapterIds = chapters.map(c => c.id);

      // Fetch flashcard titles if we have chapters
      let flashcardData: { id: string; title: string; chapter_id: string | null; order_index: number }[] = [];
      if (chapterIds.length > 0) {
        const { data } = await supabase
          .from("flashcards")
          .select("id, title, chapter_id, order_index")
          .in("chapter_id", chapterIds)
          .order("order_index", { ascending: true });
        flashcardData = data || [];
      }

      // Pre-compute counts and titles
      const counts: Record<string, number> = {};
      const titles: Record<string, { id: string; title: string }[]> = {};
      
      for (const fc of flashcardData) {
        if (fc.chapter_id) {
          counts[fc.chapter_id] = (counts[fc.chapter_id] || 0) + 1;
          if (!titles[fc.chapter_id]) titles[fc.chapter_id] = [];
          titles[fc.chapter_id].push({ id: fc.id, title: fc.title });
        }
      }

      return {
        course: courseRes.data,
        chapters,
        flashcardCounts: counts,
        flashcardTitles: titles,
      };
    },
    staleTime: 1000 * 60 * 5,
  });

}

/**
 * OPTIMIZED: Prefetch browse page data with parallel queries.
 */
function prefetchBrowseData(queryClient: QueryClient): void {
  if (prefetchedData.has("browse-data")) return;
  prefetchedData.add("browse-data");

  // Prefetch JS chunk
  prefetchRoute("/browse");

  // Prefetch published courses and stats in PARALLEL
  void Promise.all([
    queryClient.prefetchQuery({
      queryKey: ["courses", "published", "v3"],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("courses")
          .select("id, title, description, cover_image_url, is_published, order_index, tags, category, sort_priority, created_at, updated_at")
          .eq("is_published", true)
          .order("order_index", { ascending: true });
        if (error) throw error;
        return data;
      },
      staleTime: 1000 * 60 * 5,
    }),
    queryClient.prefetchQuery({
      queryKey: ["course-stats-v2"],
      queryFn: async () => {
        // Use optimized RPC functions for server-side aggregation
        const [statsRes, mapRes] = await Promise.all([
          supabase.rpc("get_course_stats"),
          supabase.rpc("get_chapter_course_map"),
        ]);

        if (statsRes.error) throw statsRes.error;
        if (mapRes.error) throw mapRes.error;

        const chapterToCourse: Record<string, string> = {};
        const chapterCounts: Record<string, number> = {};
        const flashcardCounts: Record<string, number> = {};

        for (const row of mapRes.data || []) {
          chapterToCourse[row.chapter_id] = row.course_id;
        }

        for (const row of statsRes.data || []) {
          chapterCounts[row.course_id] = Number(row.chapter_count);
          flashcardCounts[row.course_id] = Number(row.flashcard_count);
        }

        return { chapterToCourse, chapterCounts, flashcardCounts };
      },
      staleTime: 1000 * 60 * 10,
    }),
  ]);
}

/**
 * Hook for prefetching routes on user intent (hover, touch, focus).
 */
export function usePrefetchRoute(path: string) {
  const prefetchedRef = useRef(false);
  
  const triggerPrefetch = useCallback(() => {
    if (prefetchedRef.current) return;
    prefetchedRef.current = true;
    prefetchRoute(path);
  }, [path]);
  
  return {
    onMouseEnter: triggerPrefetch,
    onTouchStart: triggerPrefetch,
    onFocus: triggerPrefetch,
  };
}

/**
 * Prefetch quiz learn set data for instant navigation.
 */
function prefetchQuizLearnSetData(queryClient: QueryClient, setId: string): void {
  const key = `quiz-learn-set-${setId}`;
  if (prefetchedData.has(key)) return;
  prefetchedData.add(key);

  // Prefetch JS chunk
  prefetchRoute("/quiz-learn");

  // Prefetch quiz items using the SAME cache key as useQuizLearnItems hook
  void queryClient.prefetchQuery({
    queryKey: ["quiz-learn-items", setId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quiz_learn_items")
        .select("*")
        .eq("set_id", setId)
        .order("order_index", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * OPTIMIZED: Prefetch learning path item data based on item type.
 * - For courses: prefetch course data AND first chapter's flashcards
 * - For quiz sets: prefetch quiz learn set with items
 */
function prefetchLearningPathItem(
  queryClient: QueryClient, 
  itemType: "course" | "quiz_learn_set", 
  itemId: string
): void {
  if (itemType === "course") {
    prefetchCourseData(queryClient, itemId);
    // Also prefetch the first chapter for instant start
    prefetchFirstChapter(queryClient, itemId);
  } else {
    prefetchQuizLearnSetData(queryClient, itemId);
  }
}

/**
 * Prefetch the first chapter of a course for instant "Start Learning" flow.
 */
function prefetchFirstChapter(queryClient: QueryClient, courseId: string): void {
  const key = `first-chapter-${courseId}`;
  if (prefetchedData.has(key)) return;
  prefetchedData.add(key);

  // First get the first chapter, then prefetch its data
  void (async () => {
    try {
      const { data: chapters } = await supabase
        .from("chapters")
        .select("id")
        .eq("course_id", courseId)
        .order("order_index", { ascending: true })
        .limit(1);

      if (chapters && chapters.length > 0) {
        prefetchChapterData(queryClient, chapters[0].id);
      }
    } catch {
      // Ignore prefetch errors
    }
  })();
}

/**
 * Hook to prefetch learning path items on page load.
 * Prefetches the first item automatically for instant navigation.
 */
function usePrefetchLearningPathItems(items: Array<{ item_type: "course" | "quiz_learn_set"; item_id: string }> | undefined) {
  const queryClient = useQueryClient();
  const prefetchedRef = useRef(false);

  useEffect(() => {
    if (!items || items.length === 0 || prefetchedRef.current) return;
    prefetchedRef.current = true;

    // Use requestIdleCallback for non-blocking prefetch
    const prefetchItems = () => {
      // Prefetch first item immediately for instant "Continue" button
      if (items[0]) {
        prefetchLearningPathItem(queryClient, items[0].item_type, items[0].item_id);
      }
      
      // Prefetch second item with delay (likely next user action)
      if (items[1]) {
        setTimeout(() => {
          prefetchLearningPathItem(queryClient, items[1].item_type, items[1].item_id);
        }, 500);
      }
    };

    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(prefetchItems);
    } else {
      setTimeout(prefetchItems, 100);
    }
  }, [items, queryClient]);
}

export { 
  prefetchRoute, 
  prefetchCourseData, 
  prefetchBrowseData, 
  prefetchChapterData,
  prefetchQuizLearnSetData,
  prefetchLearningPathItem,
  prefetchFirstChapter,
  usePrefetchLearningPathItems,
};
